import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import { layerActions } from "../../store";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import StyledTooltip from "../UI/StyledTooltip";

import Dropdown from "../UI/Dropdown";
import LayerGroupArrow from "../UI/LayerGroupArrow";

import classes from "./PropertyControl.module.css";
import { Typography } from "@mui/material";

const PropertyControl = ({ layerId, orderId, propertyId }) => {
  // console.log(propertyId);
  const dispatch = useDispatch();
  const activeFilters =
    useSelector((state) => state.layers[layerId].activeFilters) || {};
  const colorValue = useSelector((state) => state.layers[layerId].colorValue);
  const dataName = useSelector((state) => state.layers[layerId].data);
  const dataState = useSelector((state) => state.data[dataName]);
  const property = useSelector((state) =>
    state.layers[layerId].properties.find((p) => p.id === propertyId)
  );

  const filterActive = Object.keys(activeFilters).includes(propertyId);
  const valuesSet = new Set();
  dataState.data.features.forEach((feat) => {
    if (Array.isArray(feat.properties[propertyId])) {
      feat.properties[propertyId].forEach((val) => {
        valuesSet.add(val);
      });
    } else {
      valuesSet.add(feat.properties[propertyId]);
    }
  });
  let uniqueValues = [...valuesSet];
  uniqueValues = uniqueValues.filter((val) => val !== "No Data");

  const valueOptions = uniqueValues
    .map((val) => {
      if (
        property.checkForValueLabels &&
        dataState.metadata &&
        dataState.metadata["value_labels"]
      ) {
        return {
          value: val,
          label: dataState.metadata["value_labels"][colorValue][val],
        };
      } else {
        return { value: val, label: val };
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  let activeFilterValues = [];
  if (activeFilters[propertyId]) {
    activeFilterValues = valueOptions
      .filter((option) => activeFilters[propertyId].includes(option.value))
      .map((option) => option.label);
  }
  const [filterValues, setFilterValues] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const defaultFilterValues = activeFilterValues
    ? activeFilterValues.map((val) => ({ label: val, value: val }))
    : null;

  const handleToggle = () => {
    setExpanded((prevState) => !prevState);
  };

  const handleFilterChange = (e) => {
    const newValues = [];
    e.forEach((obj) => newValues.push(obj.value));
    setFilterValues(newValues);
  };

  const handleApplyFilter = () => {
    dispatch(
      layerActions.addFilter({
        id: layerId,
        filterProperty: property.id,
        filterValues,
      })
    );
  };

  const handleClearFilter = () => {
    dispatch(
      layerActions.removeFilter({
        id: layerId,
        key: property.id,
      })
    );
  };

  const handleColorChange = () => {
    dispatch(
      layerActions.changeColorValue({
        id: layerId,
        colorValue: property.id,
      })
    );
  };

  return property ? (
    <Box>
      <div
        className={`${classes["filter-control-header"]} ${classes["layer-filter"]}`}
        id={orderId}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            ml: 1,
          }}
        >
          {property.colorable && (
            <input
              className={`color-radio ${classes["color-radio"]}`}
              type="radio"
              checked={colorValue === property.id}
              onChange={handleColorChange}
            />
          )}
          <LayerGroupArrow handleClick={handleToggle} show={expanded} />
          <Typography sx={{ fontSize: "0.8rem" }} onClick={handleToggle}>
            {property.label}
          </Typography>

          {property.description && (
            <StyledTooltip
              title={
                <ReactMarkdown
                  children={property.description}
                  rehypePlugins={[rehypeRaw]}
                />
              }
            >
              <IconButton onClick={null} sx={{ p: 0 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </StyledTooltip>
          )}
        </Box>
        {expanded &&
          Array.isArray(filterValues) &&
          filterValues.length > 0 &&
          (!filterActive ||
            JSON.stringify(filterValues) !==
              JSON.stringify(activeFilterValues)) && (
            <Button
              className={classes["add-filter-button"]}
              onClick={handleApplyFilter}
              variant="outlined"
              size="small"
              color="secondary"
              sx={{ borderRadius: 0 }}
            >
              Apply
            </Button>
          )}
        {filterActive && (
          <IconButton
            onClick={handleClearFilter}
            color="primary"
            sx={{ p: "4px" }}
          >
            <HighlightOffIcon />
          </IconButton>
        )}
      </div>

      {expanded && (
        <Box sx={{ mt: 0, mb: 0, ml: 4 }}>
          <Dropdown
            isMulti={true}
            options={valueOptions}
            defaultValue={defaultFilterValues}
            selectedValue={filterValues}
            handleDropdownChange={handleFilterChange}
            closeMenuOnSelect={false}
          />
        </Box>
      )}
    </Box>
  ) : null;
};

export default PropertyControl;
